import {HTMLProps, memo, ReactElement, useRef} from 'react';
import LeftIcon from './header/LeftIcon';
import SingleText from './SingleText';
import ICON_CLOSE_LIGHT from '../../../public/static/v4/common/icon-close-light.svg';
import ICON_CLOSE from '../../../public/static/v4/common/icon-close.svg';
import HeaderIcon from '../../commonSub/component/HeaderIcon';
import useIsomorphicLayoutEffect from '../hook/useIsomorphicLayoutEffect';
import useTheme from '../hook/useTheme';
import {IButtonIcon} from '../type';
import {routeBack} from '../util/PageRouter';

export interface IProps extends Omit<HTMLProps<HTMLDivElement>, 'title'> {
  title?: string | ReactElement;
  titleTextClassName?: string;
  leftElement?: ReactElement;
  rightElement?: ReactElement;
  buttonIcon?: IButtonIcon;
  isOnlyDarkMode?: boolean;
  onClick?: () => void;
}

function Header({
  title,
  titleTextClassName = 's17-bold-white light:text-black',
  leftElement,
  rightElement,
  buttonIcon,
  onClick,
  className,
  isOnlyDarkMode = false,
  ...rest
}: IProps) {
  const titleRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const {isLightMode} = useTheme();

  useIsomorphicLayoutEffect(() => {
    const titleEl = titleRef.current;
    const leftEl = leftRef.current;
    const rightEl = rightRef.current;
    if (!titleEl || (!leftEl && !rightEl)) return;

    const titleMarginX =
      Math.max(leftEl?.offsetWidth ?? 0, rightEl?.offsetWidth ?? 0) +
      TITLE_ICON_DISTANCE;

    titleEl.style.margin = `0 ${titleMarginX}px`;
  }, [title, buttonIcon, leftElement, rightElement]);

  return (
    <div
      className={cn(
        'px-18 m-auto',
        'items-center flex h-header-height',
        'fixed top-0 left-0 right-0',
        'z-navigationBar',
        className
      )}
      {...rest}
    >
      {typeof title === 'string' ? (
        <div ref={titleRef} className="px-18 absolute inset-0 flex-center">
          <SingleText
            className={cn(
              'text-center',
              isOnlyDarkMode
                ? titleTextClassName
                : `${titleTextClassName} light:text-black`
            )}
          >
            {title}
          </SingleText>
        </div>
      ) : (
        title
      )}
      <div
        ref={leftRef}
        className={cn('relative cursor-pointer', 'flex-none', {
          'py-13': buttonIcon === 'back',
        })}
        onClick={onClick || (() => routeBack())}
      >
        {leftElement}
        {!leftElement && buttonIcon && buttonIcon !== 'close' && (
          <LeftIcon icon={buttonIcon} isOnlyDarkMode={isOnlyDarkMode} />
        )}
      </div>
      <div ref={rightRef} className="ml-auto flex flex-none">
        {rightElement}
        {!rightElement && buttonIcon === 'close' && (
          <HeaderIcon
            src={isOnlyDarkMode || !isLightMode ? ICON_CLOSE : ICON_CLOSE_LIGHT}
            alt="close"
            width="24px"
            height="24px"
            onClick={onClick || (() => routeBack())}
          />
        )}
      </div>
    </div>
  );
}

const TITLE_ICON_DISTANCE = 20;

export default memo(Header);
